import React, { useEffect, useRef, useState } from "react";
import { Mic, Video } from "lucide-react";

export function useDevices(ready) {
  const [list, setList] = useState([]);
  useEffect(() => {
    if (!navigator.mediaDevices?.enumerateDevices) return undefined;
    const load = () => navigator.mediaDevices.enumerateDevices().then(setList).catch(() => {});
    load();
    navigator.mediaDevices.addEventListener?.("devicechange", load);
    return () => navigator.mediaDevices.removeEventListener?.("devicechange", load);
  }, [ready]);
  return { cams: list.filter((d) => d.kind === "videoinput"), mics: list.filter((d) => d.kind === "audioinput") };
}

export function MicMeter({ stream }) {
  const bar = useRef(null);
  const trackId = stream?.getAudioTracks()[0]?.id;
  useEffect(() => {
    const t = stream?.getAudioTracks()[0];
    if (!t) return undefined;
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const an = ctx.createAnalyser();
    an.fftSize = 512;
    ctx.createMediaStreamSource(new MediaStream([t])).connect(an);
    const buf = new Uint8Array(an.fftSize);
    let raf;
    const tick = () => {
      an.getByteTimeDomainData(buf);
      let sum = 0;
      for (const v of buf) sum += ((v - 128) / 128) ** 2;
      const lvl = Math.min(1, Math.sqrt(sum / buf.length) * 4);
      if (bar.current) bar.current.style.width = `${Math.round(lvl * 100)}%`;
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => { cancelAnimationFrame(raf); ctx.close().catch(() => {}); };
  }, [trackId]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="flex items-center gap-2" data-testid="mic-meter"><Mic className="w-4 h-4 shrink-0 opacity-70" />
      <div className="flex-1 h-2 rounded-full bg-black/10 overflow-hidden"><div ref={bar} className="h-full bg-emerald-500 transition-[width] duration-75" style={{ width: 0 }} data-testid="mic-meter-bar" /></div>
    </div>
  );
}

function Pick({ icon: Icon, items, value, onChange, label, id }) {
  return (
    <label className="flex items-center gap-2 text-sm"><Icon className="w-4 h-4 shrink-0 opacity-70" />
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="flex-1 min-w-0 rounded-full border border-black/15 bg-white px-3 py-2 text-sm text-[var(--ink)]" aria-label={label} data-testid={id}>
        {!items.length && <option value="">{label}</option>}
        {items.map((d, i) => <option key={d.deviceId || i} value={d.deviceId}>{d.label || `${label} ${i + 1}`}</option>)}
      </select>
    </label>
  );
}

// Camera + mic check: live preview, device pickers and a level meter.
export function DevicePreview({ stream, onPick, error = "", compact = false }) {
  const v = useRef(null);
  const { cams, mics } = useDevices(stream);
  const cur = (k) => stream?.getTracks().find((t) => t.kind === k)?.getSettings?.().deviceId || "";
  useEffect(() => { if (v.current) v.current.srcObject = stream || null; }, [stream]);
  return (
    <div className={`bg-white text-[var(--ink)] rounded-3xl border border-[var(--line)] p-4 space-y-3 ${compact ? "w-full max-w-sm" : "max-w-md"}`} data-testid="device-preview">
      <div className="relative aspect-video rounded-2xl overflow-hidden bg-[#111]">
        {stream ? <video ref={v} autoPlay playsInline muted className="w-full h-full object-cover -scale-x-100" data-testid="device-preview-video" />
          : <div className="absolute inset-0 flex items-center justify-center text-white/60 text-xs px-6 text-center" data-testid="device-preview-empty">{error || "Starting your camera…"}</div>}
        <span className="absolute left-2 bottom-2 text-[10px] uppercase tracking-[0.18em] bg-black/55 text-white px-2 py-0.5 rounded-full">Only you can see this</span>
      </div>
      <Pick icon={Video} items={cams} value={cur("video")} onChange={(id) => onPick("video", id)} label="Camera" id="device-pick-camera" />
      <Pick icon={Mic} items={mics} value={cur("audio")} onChange={(id) => onPick("audio", id)} label="Microphone" id="device-pick-mic" />
      <MicMeter stream={stream} />
      <p className="text-[11px] text-[var(--muted)]">Say something. The green bar moves when your mic picks you up.</p>
    </div>
  );
}
